import React from "react";
import PropTypes from "prop-types";
import ContentGrid from "arena/components/ContentGrid";
import ContentCarousel from "arena/components/ContentCarousel";
import HomepageTitle from "arena/components/HomepageTitle";
import LoadMore from "core/components/LoadMore";
import HeaderMoreLink from "arena/components/HeaderMoreLink";
import ShelfTitleLink from "arena/components/ShelfTitleLink/ShelfTitleLink";
import Box from "@mui/material/Box";
import { useQuery } from "@apollo/client";
import { ArenaContinueReadContentsQuery } from "arena/queries";
import ReactGA from "react-ga4";
import { useArena } from "core/components/ArenaProvider";

const components = {
  CAROUSEL: ContentCarousel,
  GRID: ContentGrid,
};

export default function ArenaContinueReadContents({
  title,
  view = "CAROUSEL",
  skip,
  limit = 12,
  xs = 6,
  sm = 6,
  md = 4,
  lg,
  xl,
  onCompleted,
  loadMore,
  itemsPerRow,
  previewMode,
  moreUrlHref,
}) {
  const variables = {};

  const arena = useArena();
  itemsPerRow = itemsPerRow || arena.defaultRowSize;

  if (skip) {
    variables.skip = skip;
  }

  if (limit) {
    variables.limit = limit;
  }

  const { data, loading, fetchMore } = useQuery(
    ArenaContinueReadContentsQuery,
    {
      fetchPolicy: "cache-and-network",
      variables,
      onCompleted,
    }
  );

  const Component = components[view];

  const count = data?.continueReadContents?.count || 0;
  const nodes = data?.continueReadContents?.nodes || [];
  const nodesWithReaderLinks = nodes.map((content) => {
    if (content?.type !== "BOOK" || !content?.siteUrl) {
      return content;
    }

    const hasReadSuffix = content.siteUrl.endsWith("/read");
    return {
      ...content,
      siteUrl: hasReadSuffix ? content.siteUrl : `${content.siteUrl}/read`,
    };
  });

  if (nodes.length === 0) return null;

  const hasMore = nodes.length < count;
  const showLoadMore = loadMore && hasMore && !loading && view !== "CAROUSEL";
  const nextPage = Math.ceil(nodes.length / limit);

  const onLoadMore = () => {
    ReactGA.event({
      category: "ArenaContinueReadContents",
      action: "Fetch more contents",
      label: nextPage + " page",
    });

    fetchMore({
      variables: {
        skip: nextPage * limit,
      },
    });
  };

  if (itemsPerRow) {
    sm = Math.max(12 / itemsPerRow, 4);
    md = Math.max(12 / itemsPerRow, 3);
    lg = 12 / itemsPerRow;
    xl = lg;
  }

  const trimmedMoreUrlHref = moreUrlHref?.trim() || null;
  const moreLink = trimmedMoreUrlHref || (hasMore ? "/reads" : null);

  let titleElement = title;
  if (trimmedMoreUrlHref && typeof title === "string") {
    titleElement = (
      <ShelfTitleLink url={trimmedMoreUrlHref}>{title}</ShelfTitleLink>
    );
  }

  return (
    <>
      {title ? (
        <HomepageTitle
          title={titleElement}
          action={moreLink && <HeaderMoreLink url={moreLink} />}
        />
      ) : (
        <Box sx={{ mt: 2 }} />
      )}

      <Component
        nodes={nodesWithReaderLinks}
        xs={xs}
        sm={sm}
        md={md}
        lg={lg}
        xl={xl}
        previewMode={previewMode}
      />

      {showLoadMore && <LoadMore key={nextPage} onLoadMore={onLoadMore} />}
    </>
  );
}

ArenaContinueReadContents.propTypes = {
  itemsPerRow: PropTypes.oneOf([2, 4, 3, 6]),
  xs: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  title: PropTypes.string,
  view: PropTypes.string,
  limit: PropTypes.number,
  skip: PropTypes.number,
  onCompleted: PropTypes.func,
  loadMore: PropTypes.bool,
  previewMode: PropTypes.oneOf(["HORIZONTAL", "VERTICAL"]),
  moreUrlHref: PropTypes.string,
};
